/**
 * pi-nonograms - A game of nonograms
 * https://github.com/DomenicoDeFelice/Pi-Nonograms
 *
 * Copyright (c) 2013-2020 Domenico De Felice
 * Released under the GNU GPL v3 License
 *
 * @license
 * @format
 * @flow
 */

import type { Cell } from './Model.js';

const Srand = require('seeded-rand');
const DragHelper = require('./DragHelper.js');
const Event = require('./Event.js');
const Model = require('./Model.js');
const View = require('./View.js');
const Controller = require('./Controller.js');

export type Mode = 'draw' | 'play';

export type GameOptions = $Shape<{
  width: number,
  height: number,
  mode: Mode,
  theme: string,
  srand: Srand,
  onSolved: Function,
}>;

const OptionsDefaults: GameOptions = {
  width: 10,
  height: 10,
  mode: 'play',
  theme: 'classic',
  srand: Srand,
  onSolved: function () {
    alert('Congratulations! Nonogram solved!');
  },
};

class Nonogram {
  _model: Model;
  _view: View;

  constructor(container: HTMLElement, opts?: GameOptions) {
    opts = opts ?? {};
    this._model = new Model({
      width: opts.width ?? OptionsDefaults.width,
      height: opts.height ?? OptionsDefaults.height,
      srand: opts.srand ?? OptionsDefaults.srand,
      mode: opts.mode ?? OptionsDefaults.mode,
    });
    this._model.events.nonogramSolved.attach(
      opts.onSolved ?? OptionsDefaults.onSolved
    );

    this._view = new View(this._model, container);
    this._view.setTheme(opts.theme ?? OptionsDefaults.theme);

    new Controller(this._model, this._view);
  }

  show = (): void => {
    this._view.show();
  };

  randomize = (density?: number = 0.6): void => {
    this._model.randomize(density);
  };

  giveHint = (): void => {
    this._model.giveHint();
  };

  startOver = (): void => {
    this._model.resetGuesses();
  };

  setTheme = (theme: string): void => {
    this._view.setTheme(theme);
  };

  getMode = (): Mode => {
    return this._model.getMode();
  };

  setMode = (mode: Mode): void => {
    this._model.setMode(mode);
  };
}

module.exports = Nonogram;
